import React, { useState, useEffect } from 'react';
import mondaySdk from 'monday-sdk-js';
import './App.css';

const monday = mondaySdk();

function App() {
  const [context, setContext] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [columns, setColumns] = useState([]);
  const [manualBoardId, setManualBoardId] = useState(''); // For standalone use
  const [settings, setSettings] = useState({
    backendUrl: process.env.REACT_APP_BACKEND_URL || 'https://be.podflorida.com/backend'
  });

  useEffect(() => {
    // Check if we're running inside Monday.com
    const isInMondayEnvironment = window.location.hostname !== 'localhost';
    
    if (isInMondayEnvironment) {
      // Initialize monday SDK and get context
      monday.listen('context', (res) => {
        setContext(res.data);
        if (res.data?.boardId) {
          fetchBoardColumns(res.data.boardId);
        }
      });

      // Set app theme
      monday.listen('settings', (res) => {
        if (res.data) {
          setSettings(prev => ({ ...prev, ...res.data }));
        }
      });

      // Get initial context
      monday.get('context').then((res) => {
        setContext(res.data);
        if (res.data?.boardId) {
          fetchBoardColumns(res.data.boardId);
        }
      }).catch((err) => {
        console.log('Not running in Monday.com environment');
        setContext({ boardId: null, itemId: null });
      });
    } else {
      // Local development - set mock context
      console.log('Running in local development mode');
      setContext({ 
        boardId: null, 
        itemId: null, 
        user: { id: 'dev-user' },
        account: { id: 'dev-account' }
      });
    }
  }, []);

  const fetchBoardColumns = async (boardId) => {
    try {
      const query = `query($boardId: ID!) {
        boards(ids: [$boardId]) {
          columns {
            id
            title
            type
          }
        }
      }`;

      const response = await monday.api(query, { variables: { boardId } });
      
      if (response.data && response.data.boards && response.data.boards[0]) {
        const boardColumns = response.data.boards[0].columns.filter(
          col => col.type === 'text' || col.type === 'long-text'
        );
        setColumns(boardColumns);
        if (boardColumns.length > 0) {
          setSelectedColumn(boardColumns[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching columns:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setLoading(true);
    setError('');
    setResponse('');

    try {
      // Prepare context data for the backend
      const contextData = {
        boardId: manualBoardId || context?.boardId, // Use manual input or Monday.com context
        itemId: context?.itemId,
        userId: context?.user?.id,
        accountId: context?.account?.id,
        prompt: prompt.trim(),
        selectedColumn: selectedColumn
      };

      // Get current item data if we have an item context
      let itemData = null;
      if (context?.itemId) {
        const itemQuery = `query($itemId: ID!) {
          items(ids: [$itemId]) {
            id
            name
            column_values {
              id
              text
              value
            }
          }
        }`;
        
        const itemResponse = await monday.api(itemQuery, { 
          variables: { itemId: context.itemId } 
        });
        
        if (itemResponse.data?.items?.[0]) {
          itemData = itemResponse.data.items[0];
        }
      }

      // Call backend PHP endpoint
      const backendResponse = await fetch(`${settings.backendUrl}/ai-assistant.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_MONDAY_TOKEN}`,
        },
        body: JSON.stringify({
          ...contextData,
          itemData: itemData
        })
      });

      if (!backendResponse.ok) {
        throw new Error(`Backend error: ${backendResponse.status}`);
      }

      const result = await backendResponse.json();
      
      if (result.error) {
        throw new Error(result.error);
      }

      setResponse(result.response);

      // If we have a column selected and item context, update the monday board
      if (selectedColumn && context?.itemId && result.response) {
        await updateMondayColumn(context.itemId, selectedColumn, result.response);
      }

      // Show success notification
      monday.execute('notice', {
        message: 'AI response generated successfully!',
        type: 'success'
      });

    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'An error occurred while processing your request.');
      
      monday.execute('notice', {
        message: 'Error: ' + (err.message || 'Failed to process request'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateMondayColumn = async (itemId, columnId, value) => {
    try {
      const mutation = `mutation($itemId: ID!, $columnId: String!, $value: JSON!) {
        change_column_value(item_id: $itemId, column_id: $columnId, value: $value) {
          id
        }
      }`;

      await monday.api(mutation, {
        variables: {
          itemId,
          columnId,
          value: JSON.stringify(value)
        }
      });
    } catch (err) {
      console.error('Error updating column:', err);
    }
  };

  const clearResponse = () => {
    setResponse('');
    setError('');
  };

  return (
    <div className="ai-assistant-app">
      <div className="app-header">
        <h2>🤖 AI Assistant</h2>
        <p>Ask questions about your board data or get AI-powered insights</p>
      </div>

      <form onSubmit={handleSubmit} className="prompt-form">
        <div className="form-group">
          <label htmlFor="board-id">Monday.com Board ID (for standalone use):</label>
          <input
            id="board-id"
            type="text"
            value={manualBoardId}
            onChange={(e) => setManualBoardId(e.target.value)}
            placeholder="e.g., 750779471 (get from Monday.com board URL)"
            className="prompt-input"
            style={{ marginBottom: '10px' }}
          />
          <small style={{ color: '#676879', fontSize: '12px' }}>
            Find this in your Monday.com board URL: monday.com/boards/[BOARD_ID]
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="prompt">Your Question or Prompt:</label>
          <textarea
            id="prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Ask anything about this board, item, or request an analysis..."
            rows={4}
            disabled={loading}
            className="prompt-input"
          />
        </div>

        {columns.length > 0 && (
          <div className="form-group">
            <label htmlFor="column-select">Update Column (optional):</label>
            <select
              id="column-select"
              value={selectedColumn}
              onChange={(e) => setSelectedColumn(e.target.value)}
              className="column-select"
            >
              <option value="">Don't update any column</option>
              {columns.map(col => (
                <option key={col.id} value={col.id}>
                  {col.title} ({col.type})
                </option>
              ))}
            </select>
          </div>
        )}

        <button 
          type="submit" 
          disabled={loading || !prompt.trim()}
          className="submit-button"
        >
          {loading ? '🔄 Processing...' : '✨ Ask AI'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
          <button onClick={clearResponse} className="clear-button">×</button>
        </div>
      )}

      {response && (
        <div className="response-container">
          <div className="response-header">
            <h3>AI Response:</h3>
            <button onClick={clearResponse} className="clear-button">Clear</button>
          </div>
          <div className="response-content">
            {response}
          </div>
        </div>
      )}

      {context && (
        <div className="debug-info">
          <details>
            <summary>Debug Info (Context: {context.boardId ? 'Monday.com' : 'Local Dev'})</summary>
            <pre>{JSON.stringify(context, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

export default App;